import { Link, useNavigate } from "react-router-dom";
import { mediaUrl } from "@/lib/media";
import { MapPin, Wifi, Music2, CircleDot, Video, Bookmark, MessageSquare } from "lucide-react";

export function CallingCard({ profile, index = 0, saved, onToggleSave, onMessage, isSelf = false }) {
  const avatar = mediaUrl(profile.avatar);
  const navigate = useNavigate();

  const stop = (e, fn) => { e.preventDefault(); e.stopPropagation(); fn(); };

  return (
    <Link
      to={`/profile/${profile.user_id}`}
      data-testid={`calling-card-${index}`}
      className="group block border border-line bg-surface hover:border-gold transition-colors animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="aspect-[4/3] bg-ink relative overflow-hidden">
        {avatar ? (
          <img src={avatar} alt={profile.name} className="w-full h-full object-cover media-grayscale" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-line"><Music2 size={48} /></div>
        )}
        {isSelf && (
          <span data-testid="own-card-badge" className="absolute top-3 left-3 bg-gold text-ink font-mono text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1">
            Your card
          </span>
        )}
        {!isSelf && onToggleSave && (
          <button
            onClick={(e) => stop(e, () => onToggleSave(profile.user_id))}
            data-testid={`save-card-btn-${profile.user_id}`}
            aria-label={saved ? "Remove from playlist" : "Save to playlist"}
            className={`absolute top-3 right-3 w-11 h-11 flex items-center justify-center backdrop-blur transition-colors ${saved ? "bg-gold text-ink" : "bg-ink/70 text-cream hover:text-gold"}`}
          >
            <Bookmark size={16} fill={saved ? "currentColor" : "none"} />
          </button>
        )}
        {profile.open_to_collaborate && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-ink/80 backdrop-blur text-cream font-mono text-[10px] px-2 py-1 uppercase tracking-wider">
            <CircleDot size={10} className="text-gold" /> Open
          </span>
        )}
        {profile.intro_video && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-ink/80 backdrop-blur text-cream font-mono text-[10px] px-2 py-1 uppercase tracking-wider">
            <Video size={10} className="text-gold" /> Intro
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3">
        <h3 className="font-heading text-xl font-bold tracking-tight text-cream group-hover:text-gold transition-colors">{profile.name}</h3>
        <div className="flex flex-wrap gap-1.5">
          {(profile.roles || []).slice(0, 2).map((r) => (
            <span key={r} className="border border-line px-2 py-0.5 font-mono text-[10px] uppercase text-ash">{r}</span>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(profile.genres || []).slice(0, 3).map((g) => (
            <span key={g} className="font-mono text-[10px] uppercase tracking-wider text-gold/80">{g}</span>
          ))}
        </div>
        <div className="flex items-center gap-4 pt-2 mt-1 border-t border-line font-mono text-[11px] text-ash uppercase tracking-wider">
          {profile.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{profile.location}</span>}
          {profile.remote_friendly && <span className="inline-flex items-center gap-1"><Wifi size={12} />Remote</span>}
        </div>
        {isSelf ? (
          <button
            onClick={(e) => stop(e, () => navigate("/profile"))}
            data-testid="edit-own-card-btn"
            className="mt-1 inline-flex items-center justify-center gap-2 border border-line text-ash font-mono text-[11px] uppercase tracking-widest py-3.5 hover:border-gold hover:text-gold transition-colors"
          >
            Edit my card
          </button>
        ) : onMessage && (
          <button
            onClick={(e) => stop(e, () => (onMessage ? onMessage(profile.user_id) : navigate(`/messages?to=${profile.user_id}`)))}
            data-testid={`message-card-btn-${profile.user_id}`}
            className="mt-1 inline-flex items-center justify-center gap-2 border border-gold text-gold font-mono text-[11px] uppercase tracking-widest py-3.5 hover:bg-gold hover:text-ink transition-colors"
          >
            <MessageSquare size={13} /> Message
          </button>
        )}
      </div>
    </Link>
  );
}
